// Image utility functions adapted from soloveggies-nextjs
// Purpose: construct public/CloudFront/S3 URLs and optimized variants.

export function getImageUrl(
  imageId: string | null | undefined,
  type: 'post' | 'category' = 'post'
): string {
  if (!imageId || imageId === 'null') {
    // Don't return the site logo as a fallback for missing content images.
    // Return an empty string so callers can render an appropriate placeholder.
    return '';
  }

  const cleanImageId = String(imageId).replace(/\.[^/.]+$/, '');

  const availableLocalImages = ['squash-trombone-01', 'tomate-cherry-sungold-01'];
  if (availableLocalImages.includes(cleanImageId)) {
    return `/generated-images/${cleanImageId}.webp`;
  }

  const generatedBase = process.env.NEXT_PUBLIC_GENERATED_IMAGES_URL;
  const s3Domain = process.env.NEXT_PUBLIC_AWS_S3_DOMAIN;
  const preferGenerated =
    String(process.env.NEXT_PUBLIC_PREFER_GENERATED_IMAGES || '').toLowerCase() === 'true';

  const isFullUrl = /^https?:\/\//i.test(String(imageId));

  if (isFullUrl && !s3Domain && !generatedBase) {
    return String(imageId);
  }

  if (!isFullUrl && !s3Domain && !generatedBase) {
    return `/generated-images/${cleanImageId}.webp`;
  }

  if (preferGenerated && generatedBase && !isFullUrl) {
    return `${generatedBase.replace(/\/$/, '')}/generated-images/${cleanImageId}.webp`;
  }

  if (!s3Domain && !generatedBase) {
    return `/generated-images/${cleanImageId}.webp`;
  }

  let imageUrl: string;
  if (isFullUrl) {
    try {
      const u = new URL(String(imageId));
      const fileName = u.pathname.split('/').pop() || '';
      if (fileName) {
        if (preferGenerated && generatedBase) {
          imageUrl = `${generatedBase.replace(/\/$/, '')}/generated-images/${fileName}`;
        } else if (s3Domain) {
          imageUrl = `${s3Domain.replace(/\/$/, '')}/garden_images/${fileName}`;
        } else {
          imageUrl = String(imageId);
        }
      } else {
        imageUrl = String(imageId);
      }
    } catch (e) {
      imageUrl = String(imageId);
    }
  } else {
    if (preferGenerated && generatedBase) {
      imageUrl = `${generatedBase.replace(/\/$/, '')}/generated-images/${cleanImageId}.webp`;
    } else if (s3Domain) {
      imageUrl = `${s3Domain.replace(/\/$/, '')}/garden_images/${cleanImageId}.jpg`;
    } else {
      imageUrl = '';
    }
  }

  return imageUrl;
}

export function getOptimizedImageUrl(
  imageId: string | null | undefined,
  type: 'post' | 'category' = 'post',
  width?: number,
  quality?: number
): string {
  if (!imageId || imageId === 'null') {
    return '';
  }

  const cleanImageId = String(imageId).replace(/\.[^/.]+$/, '');
  const availableLocalImages = ['squash-trombone-01', 'tomate-cherry-sungold-01'];
  if (availableLocalImages.includes(cleanImageId)) {
    return `/generated-images/${cleanImageId}.webp`;
  }

  const baseUrl = getImageUrl(imageId, type);
  if (!baseUrl) return '';

  if (baseUrl.startsWith('/images/') || baseUrl.startsWith('/generated-images/')) {
    return baseUrl;
  }

  if (baseUrl.startsWith('http') && (width || quality)) {
    const params: string[] = [];
    if (width) params.push(`w=${width}`);
    if (quality) params.push(`q=${quality}`);
    return `${baseUrl}${params.length ? `?${params.join('&')}` : ''}`;
  }

  return baseUrl;
}
